import { Component, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { NotificationService } from '../../../../shared/services/notification.service';
import { Location } from '../../models/location.model';
import { CreateLocation, UpdateLocation, LoadLocation } from '../../store/actions/locations.actions';
import { selectSelectedLocation, selectLocationsLoading } from '../../store/selectors/locations.selectors';

@Component({
    selector: 'app-location-form',
    templateUrl: './location-form.component.html',
    styleUrls: ['./location-form.component.css'],
    standalone: false
})
export class LocationFormComponent implements OnInit, OnDestroy {
  form!: UntypedFormGroup;
  isEditMode = false;
  locationId: string | null = null;
  isLoading$ = this.store.select(selectLocationsLoading);

  private destroy$ = new Subject<void>();

  constructor(
    private fb: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private store: Store<any>,
    private notify: NotificationService
  ) { }

  ngOnInit(): void {
    this.initForm();

    this.route.params.pipe(
      takeUntil(this.destroy$)
    ).subscribe(params => {
      this.locationId = params['id'];
      this.isEditMode = !!this.locationId;

      if (this.isEditMode && this.locationId) {
        this.store.dispatch(new LoadLocation({ id: this.locationId }));
        this.loadLocationData();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initForm(): void {
    this.form = this.fb.group({
      id: [''],
      name: ['', [Validators.required, Validators.maxLength(200)]],
      slug: ['', [Validators.required, Validators.maxLength(200)]],
      description: ['', Validators.maxLength(500)],
      addressLine1: ['', Validators.maxLength(200)],
      addressLine2: ['', Validators.maxLength(200)],
      city: ['', Validators.maxLength(100)],
      state: ['', Validators.maxLength(100)],
      postalCode: ['', Validators.maxLength(20)],
      countryCode: ['', [Validators.required, Validators.maxLength(2)]],
      timeZoneId: ['', [Validators.required, Validators.maxLength(100)]],
      currencyCode: ['', Validators.maxLength(3)],
      phone: ['', Validators.maxLength(20)],
      email: ['', [Validators.email, Validators.maxLength(100)]],
      isMainLocation: [false],
      displayOrder: [0, [Validators.min(0)]],
      status: ['Active', Validators.required]
    });
  }

  private loadLocationData(): void {
    this.store.select(selectSelectedLocation).pipe(
      takeUntil(this.destroy$)
    ).subscribe(location => {
      if (location) {
        this.form.patchValue(location);
      }
    });
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.notify.error('Por favor, complete todos los campos requeridos');
      return;
    }

    const location: Location = this.form.value;

    if (this.isEditMode) {
      this.store.dispatch(new UpdateLocation({ location }));
      this.notify.ok('Centro de trabajo actualizado correctamente');
    } else {
      this.store.dispatch(new CreateLocation({ location }));
      this.notify.ok('Centro de trabajo creado correctamente');
    }

    this.router.navigate(['/admin/reservas']);
  }

  onCancel(): void {
    this.router.navigate(['/admin/reservas']);
  }
}
